import classnames from "classnames";
import { ReactNode, useState } from "react";
import { Form } from "formik";
import MyFormik from "../Form/MyFormik.tsx";
import Button, { TYPE_BUTTON, VARIANTS } from "../Button/Button.tsx";
import styles from "./Stepper.module.css";

const Stepper = ({
  steps,
  getInitialValues,
  prepareVariables,
  validationSchema,
}: Stepper) => {
  const [activeStep, setActiveStep] = useState(steps[0]);
  const [previousStep, setPreviousStep] = useState(null);
  const [nextStep, setNextStep] = useState(steps[1] ?? null);
  const [, setIconActiveStep] = useState(null);

  return (
    <div>
      {steps.map((step, key) => (
        <div key={key}>
          <Step
            step={step}
            steps={steps}
            activeStep={activeStep}
            previousStep={previousStep}
            nextStep={nextStep}
            setActiveStep={setActiveStep}
            setPreviousStep={setPreviousStep}
            setNextStep={setNextStep}
            getInitialValues={getInitialValues}
            prepareVariables={prepareVariables}
            validationSchema={validationSchema}
            setIconActiveStep={setIconActiveStep}
          />
        </div>
      ))}
    </div>
  );
};

type Step = {
  title: string;
  content: ReactNode;
  useFormik: boolean;
  submitUrl?: string;
  skippable: boolean;
  icon?: ReactNode;
};

type Stepper = {
  steps: Step[];
  getInitialValues: (...args: any[]) => any;
  prepareVariables: (...args: any[]) => any;
  validationSchema: object;
};

export default Stepper;

type StepperSteps = {
  step: Step;
  steps: Step[];
  activeStep: Step;
  previousStep: any;
  nextStep: any;
  setActiveStep: (step: Step) => void;
  setPreviousStep: (step: Step | null) => void;
  setNextStep: (step: Step | null) => void;
  getInitialValues: (...args: any[]) => any;
  prepareVariables: (...args: any[]) => any;
  validationSchema: object;
  setIconActiveStep: (icon: object) => void;
};

export const Step = ({
  step,
  steps,
  activeStep,
  previousStep,
  nextStep,
  setActiveStep,
  setPreviousStep,
  setNextStep,
  getInitialValues,
  prepareVariables,
  validationSchema,
  setIconActiveStep,
}: StepperSteps) => {
  const { title, content, useFormik, submitUrl, skippable, icon } = step;

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.align}>
          <div
            className={classnames(styles.index, {
              [styles.active]: activeStep === step,
            })}
          >
            {steps.findIndex((e) => e === step) + 1}
          </div>
          <h2 className={styles.title}>
            {title} {icon}
          </h2>
        </div>
        {activeStep === step && skippable && (
          <Button
            variant={VARIANTS.primary}
            onClick={() => {
              setActiveStep(nextStep);
              setPreviousStep(activeStep);
              setNextStep(
                steps.findIndex((s) => s === activeStep) + 2 <= steps.length - 1
                  ? steps[steps.findIndex((s) => s === activeStep) + 2]
                  : null,
              );
              step.icon = <span className={styles.nok}> &#10060;</span>;
              setIconActiveStep(<span className={styles.nok}> &#10060;</span>);
            }}
          >
            Ignorer cette étape
          </Button>
        )}
      </div>
      <div className={styles.container}>
        <div className={styles.borderLeft}>
          {activeStep === step && (
            <div className={styles.open}>
              {useFormik === true ? (
                <MyFormik
                  initialValues={getInitialValues()}
                  validationSchema={validationSchema}
                  prepareVariables={prepareVariables}
                  submitUrl={submitUrl}
                  isMultipartFormData={true}
                  onSubmit={() => {
                    setActiveStep(nextStep);
                    setPreviousStep(activeStep);
                    setNextStep(
                      steps.findIndex((s) => s === activeStep) + 2 <=
                        steps.length - 1
                        ? steps[steps.findIndex((s) => s === activeStep) + 2]
                        : null,
                    );
                    step.icon = <span className={styles.ok}>&#10003;</span>;
                    setIconActiveStep(<>&#10003;</>);
                  }}
                  initialStatus={undefined}
                  isPost={false}
                  redirectUrl={""}
                  afficheLog={true}
                >
                  <Form>
                    {content}
                    {/*Bouton pour naviger dans le stepper */}
                    <nav className={styles.navigation}>
                      <Button
                        variant={VARIANTS.primary}
                        onClick={() => {
                          setActiveStep(previousStep);
                          setPreviousStep(
                            steps.findIndex((s) => s === activeStep) - 2 >= 0
                              ? steps[
                                  steps.findIndex((e) => e === activeStep) - 2
                                ]
                              : null,
                          );
                          setNextStep(activeStep);
                        }}
                        className={styles.precedent}
                        disabled={
                          steps.findIndex((e) => e === activeStep) - 1 < 0
                        }
                      >
                        Précédent
                      </Button>
                      <Button
                        variant={VARIANTS.primary}
                        type={TYPE_BUTTON.submit}
                        className={styles.suivant}
                        disabled={
                          steps.findIndex((e) => e === activeStep) + 1 >=
                          steps.length
                        }
                      >
                        Suivant
                      </Button>
                    </nav>
                  </Form>
                </MyFormik>
              ) : !useFormik ? (
                <>
                  {content}
                  {/*Bouton pour naviger dans le stepper */}
                  <nav className={styles.navigation}>
                    {steps.findIndex((e) => e === activeStep) - 1 > 0 ||
                      (steps.findIndex((e) => e === activeStep) ===
                        steps.length - 1 && (
                        <Button
                          variant={VARIANTS.primary}
                          onClick={() => {
                            setActiveStep(previousStep);
                            setPreviousStep(
                              steps.findIndex((s) => s === activeStep) - 2 >= 0
                                ? steps[
                                    steps.findIndex((e) => e === activeStep) - 2
                                  ]
                                : null,
                            );
                            setNextStep(activeStep);
                          }}
                          className={styles.precedent}
                          disabled={
                            steps.findIndex((e) => e === activeStep) - 1 < 0
                          }
                        >
                          Précédent
                        </Button>
                      ))}
                    {steps.findIndex((e) => e === activeStep) + 1 <
                      steps.length && (
                      <Button
                        variant={VARIANTS.primary}
                        onClick={() => {
                          setActiveStep(nextStep);
                          setPreviousStep(activeStep);
                          setNextStep(
                            steps.findIndex((s) => s === activeStep) + 2 <=
                              steps.length - 1
                              ? steps[
                                  steps.findIndex((s) => s === activeStep) + 2
                                ]
                              : null,
                          );
                          step.icon = (
                            <span className={styles.ok}>&#10003;</span>
                          );
                          setIconActiveStep(<>&#10003;</>);
                        }}
                        className={styles.suivant}
                        disabled={
                          steps.findIndex((e) => e === activeStep) + 1 >=
                          steps.length
                        }
                      >
                        Suivant
                      </Button>
                    )}
                  </nav>
                </>
              ) : (
                ""
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
